// src/components/OperatorDashboard.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getUserRole, canViewEntity } from '../utils/roleHierarchy';
import { filterEntitiesByAccess } from '../utils/assignmentSystem';
import { useModal } from '../context/ModalContext';
import Layout from './Layout';
import CalendarView from './CalendarView';
import MeetingsPanel from './MeetingsPanel';
import MeetingScheduler from './MeetingScheduler';
import ToDoList from './ToDoList';
import ContentCreation from './ContentCreation';
import CreateIdeas from './CreateIdeas';
import SourcesPanel from './SourcesPanel';
import Recordings from './Recordings';
import LiveKitRecordings from './LiveKitRecordings';
import LiveKitMeetings from './LiveKitMeetings';
import { LIVEKIT_ENABLED } from '../config/features';
import Gemini from './Gemini';
import BrandAssets from './BrandAssets';
import IdeasPanel from './IdeasPanel';
import ReviewContentPanel from './ReviewContentPanel';
import SchedulePostsPanel from './SchedulePostsPanel';
import DashboardHome from './DashboardHome';
import ProfilePanel from './ProfilePanel';
import NotificationsPanel from './NotificationsPanel';
import WelcomeModal from './WelcomeModal';
import OnboardingWizard from './OnboardingWizard';
import OnboardingRoleSelector from './OnboardingRoleSelector';
import '../styles/OperatorDashboard.css';

const OperatorDashboard = ({ user, onLogout, onUserUpdate, isProxyMode = false }) => {
  const modal = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [userAssignments, setUserAssignments] = useState(null);
  const [showOnboardingRoleSelector, setShowOnboardingRoleSelector] = useState(false);
  const [selectedOnboardingRole, setSelectedOnboardingRole] = useState(null);

  // In proxy mode, use local state instead of URL params to avoid affecting parent
  const [localActiveSection, setLocalActiveSection] = useState('dashboard');

  // Read activeSection from URL params (if not in proxy mode) or local state (if in proxy mode)
  const activeSection = isProxyMode ? localActiveSection : (searchParams.get('section') || 'dashboard');

  // Update URL or local state when section changes
  const setActiveSection = (section) => {
    if (isProxyMode) {
      setLocalActiveSection(section);
    } else {
      setSearchParams({ section });
    }
  };

  const userRole = getUserRole(user);

  useEffect(() => {
    fetchDashboardData();
    fetchUserAssignments();
  }, []);

  useEffect(() => {
    if (user && !isProxyMode) {
      if (user.welcome_seen === false) {
        setShowWelcomeModal(true);
      } else if (user.onboarding_seen === false) {
        setShowOnboardingWizard(true);
      }
    }
  }, [user, isProxyMode]);

  const handleOpenWelcome = () => {
    setShowWelcomeModal(true);
  };

  const handleWelcomeComplete = async () => {
    try {
      await axios.post('/api/user/mark-welcome-seen');
      if (user.onboarding_seen === false) {
        setShowOnboardingWizard(true);
      }
    } catch (error) {
      console.error('Failed to mark welcome as seen:', error);
    }
  };

  const handleOpenOnboarding = () => {
    setShowOnboardingWizard(true);
  };

  const handleRoleSelect = (role) => {
    setSelectedOnboardingRole(role);
    setShowOnboardingWizard(true);
  };

  const handleOnboardingComplete = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/operator/dashboard');
      setDashboardData(response.data);
      // Check for warning in response (e.g., no agency assigned)
      if (response.data?.warning) {
        setWarning(response.data.warning);
      } else {
        setWarning(null);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Don't show error in proxy mode as this may be a brief transition error
      if (!isProxyMode) {
        modal.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAssignments = async () => {
    try {
      const response = await axios.get('/api/user/assignments');
      setUserAssignments(response.data.assignments);
    } catch (error) {
      console.error('Error fetching user assignments:', error);
    }
  };

  const handleOrganizationChange = async (organization) => {
    try {
      // Organization context is already set on backend via OrganizationSelector
      // Only refresh dashboard data if we're on the dashboard view
      // Other views (like IdeasPanel) handle filtering client-side via context
      if (activeSection === 'dashboard') {
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  const getContextMenuTitle = () => {
    switch (activeSection) {
      case 'dashboard':
        return 'Dashboard';
      case 'todo':
        return 'To Do List';
      case 'create':
        return 'Generate Ideas';
      case 'recordings':
        return 'Recordings';
      case 'velvet-recordings':
        return 'Velvet Recordings';
      case 'livekit-meetings':
        return 'Velvet Meetings';
      case 'sources':
        return 'Manage Sources';
      case 'review-content':
        return 'Review Content';
      case 'calendar':
        return 'Calendar';
      case 'calendar-scheduler':
        return 'Schedule Meeting';
      case 'assigned-clients':
        return 'Assigned Clients';
      case 'assigned-organizations':
        return 'Assigned Organizations';
      case 'assigned-brands':
        return 'Assigned Brands';
      case 'gemini':
        return 'Velvet AI';
      case 'assets':
        return 'Brand Identity';
      case 'create-ideas':
        return 'Create Ideas';
      case 'operator-review-ideas':
        return 'Review Ideas';
      case 'schedule-posts':
        return 'Schedule Posts';
      default:
        return 'Dashboard';
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardHome user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'todo':
        return <ToDoList user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'create':
        return <ContentCreation user={user} />;
      case 'sources':
        return <SourcesPanel user={user} />;
      case 'recordings':
        return <Recordings user={user} />;
      case 'velvet-recordings':
        return LIVEKIT_ENABLED
          ? <LiveKitRecordings user={user} />
          : <DashboardHome user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'livekit-meetings':
        return LIVEKIT_ENABLED
          ? <LiveKitMeetings user={user} />
          : <DashboardHome user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'accessible-clients':
        return renderAccessibleClients();
      case 'assigned-organizations':
        return renderAssignedOrganizations();
      case 'assigned-brands':
        return renderAssignedBrands();
      case 'review-content':
        return <ReviewContentPanel user={user} isProxyMode={isProxyMode} />;
      case 'calendar':
      case 'calendar-scheduler':
        return <MeetingsPanel user={user} />;
      case 'gemini':
        return <Gemini user={user} />;
      case 'assets':
        return <BrandAssets user={user} />;
      case 'create-ideas':
        return <CreateIdeas user={user} />;
      case 'operator-review-ideas':
        return <IdeasPanel user={user} isProxyMode={isProxyMode} />;
      case 'schedule-posts':
        return <SchedulePostsPanel user={user} />;
      case 'profile':
        return <ProfilePanel user={user} onUserUpdate={onUserUpdate} />;
      case 'notifications':
        return <NotificationsPanel user={user} />;
      default:
        return (
          <div className="content-section">
            <div className="error-panel">
              <h2>Section Not Found</h2>
              <p>The requested section "{activeSection}" does not exist.</p>
              <button onClick={() => setActiveSection('dashboard')} className="btn-primary">
                Go to Dashboard
              </button>
            </div>
          </div>
        );
    }
  };

  const renderAccessibleClients = () => {
    // Clients are accessible based on org/brand assignments, not directly assigned
    const accessibleClients = dashboardData?.accessible_clients || [];

    return (
      <div className="content-section">
        <h2>Accessible Clients</h2>
        <p className="section-description">Clients you can work with based on your organization/brand assignments.</p>
        <div className="entity-grid">
          {accessibleClients.length > 0 ? (
            accessibleClients.map(client => (
              <div key={client.id} className="entity-card">
                <h3>{client.name}</h3>
                <div className="entity-details">
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{client.email || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Organization:</span>
                    <span className="detail-value">{client.organization_name || 'N/A'}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-assignments">
              <h3>No Accessible Clients</h3>
              <p>You don't have access to any clients yet. Get assigned to organizations or brands to work with their clients.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAssignedOrganizations = () => {
    const assignedOrganizations = dashboardData?.accessible_organizations || [];

    return (
      <div className="content-section">
        <h2>My Assigned Organizations</h2>
        <div className="entity-grid">
          {assignedOrganizations.length > 0 ? (
            assignedOrganizations.map(org => (
              <div key={org.id} className="entity-card">
                <h3>{org.name}</h3>
                <div className="entity-details">
                  <div className="detail-row">
                    <span className="detail-label">Client:</span>
                    <span className="detail-value">{org.client_name || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Brands:</span>
                    <span className="detail-value">{org.brands?.length || 0}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">
                      {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-assignments">
              <h3>No Organizations Assigned</h3>
              <p>You don't have access to any organizations. Contact your administrator to get assigned to organizations.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAssignedBrands = () => {
    const assignedBrands = dashboardData?.accessible_brands || [];

    return (
      <div className="content-section">
        <h2>My Assigned Brands</h2>
        <div className="entity-grid">
          {assignedBrands.length > 0 ? (
            assignedBrands.map(brand => (
              <div key={brand.id} className="entity-card">
                <h3>{brand.name}</h3>
                <div className="entity-details">
                  <div className="detail-row">
                    <span className="detail-label">Organization:</span>
                    <span className="detail-value">{brand.organization_name || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Client:</span>
                    <span className="detail-value">{brand.client_name || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">
                      {brand.created_at ? new Date(brand.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-assignments">
              <h3>No Brands Assigned</h3>
              <p>You don't have access to any brands. Contact your administrator to get assigned to specific brands.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout
        user={user}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onOrganizationChange={handleOrganizationChange}
        onLogout={onLogout}
        onOpenWelcome={handleOpenWelcome}
        onOpenOnboarding={handleOpenOnboarding}
        isProxyMode={isProxyMode}
        showOrgSelector={false}
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  // Determine if org/brand context is needed for current section
  // Operators don't need context for calendar/dashboard - they can see all their assignments
  const needsOrgBrandContext = () => {
    // In proxy mode, show selectors on more panels so admin can see operator's context
    if (isProxyMode) {
      const sectionsNeedingContext = [
        'dashboard',
        'todo',
        'create',
        'recordings',
        'sources',
        'create-ideas',
        'operator-review-ideas',
        'review-content',
        'schedule-posts',
        'assets',
        'subscriptions'
      ];
      return sectionsNeedingContext.includes(activeSection);
    }

    // For real operators (not proxy mode), show on specific sections
    const sectionsNeedingContext = [
      'recordings',
      'sources',
      'create-ideas',
      'operator-review-ideas',
      'review-content',
      'schedule-posts',
      'assets',
      'subscriptions',
      'create'
    ];
    return sectionsNeedingContext.includes(activeSection);
  };

  const showContextSelectors = needsOrgBrandContext();

  return (
    <>
      <Layout
        user={user}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        contextMenuContent={showContextSelectors ? <h2>{getContextMenuTitle()}</h2> : null}
        onOrganizationChange={handleOrganizationChange}
        onLogout={onLogout}
        onOpenWelcome={handleOpenWelcome}
        onOpenOnboarding={handleOpenOnboarding}
        isProxyMode={isProxyMode}
        showOrgSelector={showContextSelectors}
      >
        {warning && (
          <div className="warning-banner">
            <p>{warning}</p>
          </div>
        )}
        {renderContent()}
      </Layout>

      {!isProxyMode && (
        <>
          <WelcomeModal
            isOpen={showWelcomeModal}
            onClose={() => setShowWelcomeModal(false)}
            onComplete={handleWelcomeComplete}
          />
          <OnboardingRoleSelector
            isOpen={showOnboardingRoleSelector}
            onClose={() => setShowOnboardingRoleSelector(false)}
            onSelectRole={handleRoleSelect}
          />
          <OnboardingWizard
            isOpen={showOnboardingWizard}
            onClose={() => {
              setShowOnboardingWizard(false);
              setSelectedOnboardingRole(null);
            }}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
            user={user}
            overrideRole={selectedOnboardingRole}
            onSectionChange={setActiveSection}
            currentSection={activeSection}
          />
        </>
      )}
    </>
  );
};

export default OperatorDashboard;
